import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useSession } from '../layout/TopBar';
import { routeBase, useCollection } from '../api/collections';
import { CollectionPage } from './CollectionPage';
import { PublicCollectionPage } from './PublicCollectionPage';

/**
 * One URL per collection, whoever opens it (decided 2026-09-09: no separate
 * public path). A member of its workspace gets the editable page; anybody
 * else, guest or signed in elsewhere, gets the read-only page when the
 * collection is shared by link or public, and nothing when it is private.
 *
 * The answer is the detail read itself: it 404s for a non-member, and the
 * editable page reuses the same query, so a member pays one request.
 *
 * `?study=<uid>` is a study's share link as it was first copied (the
 * collection's URL naming the study). It goes to the study's own page, which
 * opens for anyone the study is shared with even when the collection is
 * private; the collection page never read the parameter.
 */
const UID = /^[0-9.]{1,64}$/;

export function CollectionRoute() {
  const { publicId } = useParams<{ publicId: string }>();
  const [params] = useSearchParams();
  const study = params.get('study');
  const toStudy = study && UID.test(study) ? study : null;
  const { data: me, isLoading } = useSession();
  const guest = !isLoading && !me?.userId;
  const detail = useCollection(isLoading || guest || toStudy ? undefined : publicId);
  if (toStudy) return <Navigate replace to={`${routeBase()}/studies/${encodeURIComponent(toStudy)}`} />;
  if (isLoading) return <div style={{ flex: 1 }} />;
  if (guest || detail.isError) return <PublicCollectionPage />;
  if (!detail.data) return <div style={{ flex: 1 }} />;
  // Keyed, so a new collection is a new page. Without it a CACHED collection
  // rendered into the previous one's instance and inherited its state: the
  // selection (group keys included), a half-finished rename, an open editor.
  return <CollectionPage key={publicId} />;
}
